/**
 * Utility functions for Purchase Order (PO) tracking, status determination,
 * and expiration indicators.
 */

/**
 * Determine the status of a Purchase Order given its expiry/due date.
 * 
 * @param {string|Date} poExpiryDate - Expiry or due date string (YYYY-MM-DD or ISO)
 * @param {string} [poNumber] - Optional PO number
 * @returns {object} Status object:
 *   - status: 'expired' | 'expiring_soon' | 'valid' | 'none'
 *   - isExpired: boolean
 *   - isExpiringSoon: boolean (within 7 days)
 *   - isValid: boolean
 *   - label: 'PO Expired' | 'PO Expiring Soon' | 'PO Valid' | 'No PO'
 *   - color: 'error' | 'warning' | 'success' | 'default'
 *   - diffDays: number (negative if expired, positive if in future, 0 if today)
 *   - description: e.g. "Expired 3 days ago", "Expires in 5 days", "Expires today"
 */
export const getPoStatus = (poExpiryDate, poNumber = '') => {
  if (!poExpiryDate || poExpiryDate === 'N/A' || String(poExpiryDate).trim() === '') {
    return {
      status: 'none',
      isExpired: false,
      isExpiringSoon: false,
      isValid: false,
      label: 'No PO Due Date',
      color: 'default',
      diffDays: null,
      description: 'No expiry date specified',
    };
  }

  try {
    const expiry = new Date(poExpiryDate);
    if (isNaN(expiry.getTime())) {
      return {
        status: 'none',
        isExpired: false,
        isExpiringSoon: false,
        isValid: false,
        label: 'Invalid Date',
        color: 'default',
        diffDays: null,
        description: 'Invalid expiry date',
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    const diffMs = expiry.getTime() - today.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      const daysAgo = Math.abs(diffDays);
      return {
        status: 'expired',
        isExpired: true,
        isExpiringSoon: false,
        isValid: false,
        label: 'PO Expired',
        color: 'error',
        diffDays,
        description: `Expired ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`,
      };
    } else if (diffDays <= 7) {
      return {
        status: 'expiring_soon',
        isExpired: false,
        isExpiringSoon: true,
        isValid: true,
        label: 'PO Expiring Soon',
        color: 'warning',
        diffDays,
        description: diffDays === 0 ? 'Expires today' : `Expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`,
      };
    } else {
      return {
        status: 'valid',
        isExpired: false,
        isExpiringSoon: false,
        isValid: true,
        label: 'PO Valid',
        color: 'success',
        diffDays,
        description: `Expires in ${diffDays} days`,
      };
    }
  } catch (err) {
    return {
      status: 'none',
      isExpired: false,
      isExpiringSoon: false,
      isValid: false,
      label: 'Error',
      color: 'default',
      diffDays: null,
      description: 'Unable to calculate status',
    };
  }
};
